import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  Divider,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger as ChakraPopoverTrigger,
  useToast,
  Icon,
  Tag as ChakraTag,
  TagLabel,
  HStack,
  Box,
} from "@chakra-ui/react";
import { useState } from "react";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaRegMoneyBillAlt,
  FaTags,
} from "react-icons/fa";
import { MdBusinessCenter } from "react-icons/md";
import { RiBuildingLine } from "react-icons/ri";
import { useHistory, useParams } from "react-router-dom";
import {
  ADMIN_ACTIVATE_EMPLOYER_JOB_AD,
  ADMIN_REJECT_EMPLOYER_JOB_AD,
  GET_EMPLOYER_JOB_AD,
} from "../../../features/employer-job-ads/constants";
import {
  EmployerJobAd,
  EmployerJobAdStatus,
} from "../../../features/employer-job-ads/types";
import Loading from "../../../_components/Loading";
import RequestFailed from "../../../_components/RequestFailed";
import {
  formatAsCurrency,
  formatContractJobAdDuration,
} from "../../../helpers/helpers";
import { FaRegClock } from "react-icons/fa";
import { ProsWithinReachSection } from "./ProsWithinReachSection";
import React from "react";

const PopoverTrigger: React.FC<{ children: React.ReactNode }> = ChakraPopoverTrigger;

// Helper function to convert employment type to readable text
const employmentTypeToText = (type: string) => {
  return type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export default function Page() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const toast = useToast();

  const [activateDisclosure, setActivateDisclosure] = useState(false);
  const [rejectDisclosure, setRejectDisclosure] = useState(false);

  const { data, loading, error, refetch } = useQuery<{
    getEmployerJobAd: EmployerJobAd;
  }>(GET_EMPLOYER_JOB_AD, {
    variables: { id },
  });

  const [activateJobAd, activateJobAdResponse] = useMutation(
    ADMIN_ACTIVATE_EMPLOYER_JOB_AD,
    {
      onCompleted: () => {
        toast({
          title: "Job ad activated successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        refetch();
        setActivateDisclosure(false);
      },
      onError: (error) => {
        toast({
          title: "Failed to activate job ad",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const [rejectJobAd, rejectJobAdResponse] = useMutation(
    ADMIN_REJECT_EMPLOYER_JOB_AD,
    {
      onCompleted: () => {
        toast({
          title: "Job ad rejected successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        refetch();
        setRejectDisclosure(false);
      },
      onError: (error) => {
        toast({
          title: "Failed to reject job ad",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const handleActivate = () => {
    activateJobAd({
      variables: { id },
    });
  };

  const handleReject = () => {
    rejectJobAd({
      variables: { id },
    });
  };

  if (loading) return <Loading />;

  if (error) return <RequestFailed />;

  if (!data?.getEmployerJobAd)
    return <RequestFailed errorMessage="Job ad not found" />;

  const {
    title,
    description,
    status,
    organization,
    principal,
    thirdParty,
    createdAt,
    employmentType,
    salaryDetails,
    tags,
    duration,
  } = data.getEmployerJobAd;

  // Get the organization represented name (from principal or thirdParty)
  const organizationRepresented = principal?.name || thirdParty?.name;

  const getStatusBadgeColor = (status: EmployerJobAdStatus) => {
    switch (status) {
      case EmployerJobAdStatus.IN_REVIEW:
        return "orange";
      case EmployerJobAdStatus.ACTIVE:
        return "green";
      case EmployerJobAdStatus.STOPPED:
        return "orange";
      case EmployerJobAdStatus.DISABLED:
        return "gray";
      case EmployerJobAdStatus.REJECTED:
        return "red";
      default:
        return "gray";
    }
  };

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-2 overflow-y-auto max-h-[calc(90vh-100px)]">
        <div className="flex flex-col gap-8 p-6 bg-white border">
          <div className="mb-4">
            <h2 className="text-primary-500 text-lg font-medium m-0">
              Job Ad: {title}
            </h2>
            <p className="m-0 text-primary-300 text-sm">
              Review the job ad details before approving or rejecting
            </p>
          </div>

          <div className="grid grid-cols-[1fr,2fr] gap-2 max-w-md">
            <p className="m-0 text-primary-300 text-sm">Organization</p>
            <p className="m-0 text-primary-500 text-sm font-medium">
              {organization.name}
            </p>

            {organizationRepresented && (
              <>
                <p className="m-0 text-primary-300 text-sm">Represented</p>
                <p className="m-0 text-primary-500 text-sm font-medium">
                  {organizationRepresented}
                </p>
              </>
            )}

            <p className="m-0 text-primary-300 text-sm">Created At</p>
            <p className="m-0 text-primary-500 text-sm font-medium">
              {new Date(createdAt).toLocaleString()}
            </p>

            <p className="m-0 text-primary-300 text-sm">Status</p>
            <p className="m-0 text-primary-500 text-sm font-medium">
              <Badge colorScheme={getStatusBadgeColor(status)}>
                {status.replace("_", " ")}
              </Badge>
            </p>
          </div>

          <Divider />

          <div>
            <p className="m-0 text-primary-500 text-base font-medium mb-2">
              Description
            </p>
            <div
              className="markdown"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </div>

          <Divider />
          <div className="grid grid-cols-[1fr,1fr] gap-6 lg:gap-8">
            {/* Employment Type */}
            <div className="flex items-center gap-4">
              <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                <Icon
                  as={MdBusinessCenter}
                  w={{
                    base: "20px",
                    lg: "20px",
                  }}
                  h={{
                    base: "20px",
                    lg: "20px",
                  }}
                  color={"#212121"}
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-[#6E6E6E] text-[14px]">Employment type</p>
                <p className="text-[#212121] text-[16px]">
                  {employmentTypeToText(employmentType)}
                </p>
              </div>
            </div>

            {duration && (
              <div className="flex items-center gap-4">
                <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                  <Icon
                    as={FaRegClock}
                    w={{
                      base: "20px",
                      lg: "20px",
                    }}
                    h={{
                      base: "20px",
                      lg: "20px",
                    }}
                    color={"#212121"}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-[#6E6E6E] text-[14px]">Duration</p>
                  <p className="text-[#212121] text-[16px]">
                    {formatContractJobAdDuration(duration)}
                  </p>
                </div>
              </div>
            )}

            {salaryDetails && (
              <div className="flex items-center gap-4">
                <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                  <Icon
                    as={FaRegMoneyBillAlt}
                    w={{
                      base: "20px",
                      lg: "20px",
                    }}
                    h={{
                      base: "20px",
                      lg: "20px",
                    }}
                    color={"#212121"}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-[#6E6E6E] text-[14px]">
                    {`Salary ${salaryDetails.cap ? "range" : ""}`}
                  </p>
                  <p className="text-[#212121] text-[16px]">
                    {formatAsCurrency(
                      salaryDetails.starting,
                      salaryDetails.currencyCode,
                    )}{" "}
                    {salaryDetails.cap
                      ? `- ${formatAsCurrency(
                          salaryDetails.cap,
                          salaryDetails.currencyCode,
                        )}`
                      : ""}
                  </p>
                </div>
              </div>
            )}

            {/* Tags Section */}
            {tags && tags.length > 0 && (
              <div className="flex items-start gap-4">
                <div className="border-[#ACACAC] border-[1px] p-2 lg:p-3">
                  <Icon
                    as={FaTags}
                    w={{
                      base: "20px",
                      lg: "20px",
                    }}
                    h={{
                      base: "20px",
                      lg: "20px",
                    }}
                    color={"#212121"}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-[#6E6E6E] text-[14px]">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <ChakraTag
                        size={"md"}
                        key={tag.id}
                        borderRadius="full"
                        colorScheme={"blue"}
                      >
                        <TagLabel>{tag.name}</TagLabel>
                      </ChakraTag>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <Divider />

          {/* Pros Within Reach Section */}
          <ProsWithinReachSection jobAdId={id} />
        </div>
      </div>

      <div>
        <div className="flex flex-col gap-6 p-6 bg-white border">
          <div>
            <h2 className="text-primary-500 text-lg font-medium m-0">
              Actions
            </h2>
          </div>
          <Divider />

          {status === EmployerJobAdStatus.IN_REVIEW && (
            <div className="flex flex-col gap-4">
              <Popover
                isOpen={activateDisclosure}
                onOpen={() => setActivateDisclosure(true)}
                onClose={() => setActivateDisclosure(false)}
              >
                <PopoverTrigger>
                  <Button
                    colorScheme="green"
                    leftIcon={<FaCheckCircle />}
                    isLoading={activateJobAdResponse.loading}
                    isDisabled={status !== EmployerJobAdStatus.IN_REVIEW}
                  >
                    Activate Job Ad
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader>Confirmation</PopoverHeader>
                  <PopoverBody>
                    Are you sure you want to activate this job ad? Once
                    activated, it will be visible to candidates.
                  </PopoverBody>
                  <PopoverFooter display="flex" justifyContent="flex-end">
                    <Button
                      colorScheme="green"
                      onClick={handleActivate}
                      isLoading={activateJobAdResponse.loading}
                    >
                      Confirm
                    </Button>
                  </PopoverFooter>
                </PopoverContent>
              </Popover>

              <Popover
                isOpen={rejectDisclosure}
                onOpen={() => setRejectDisclosure(true)}
                onClose={() => setRejectDisclosure(false)}
              >
                <PopoverTrigger>
                  <Button
                    colorScheme="red"
                    leftIcon={<FaTimesCircle />}
                    isLoading={rejectJobAdResponse.loading}
                    isDisabled={status !== EmployerJobAdStatus.IN_REVIEW}
                  >
                    Reject Job Ad
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader>Confirmation</PopoverHeader>
                  <PopoverBody>
                    Are you sure you want to reject this job ad? The employer
                    will be notified of the rejection.
                  </PopoverBody>
                  <PopoverFooter display="flex" justifyContent="flex-end">
                    <Button
                      colorScheme="red"
                      onClick={handleReject}
                      isLoading={rejectJobAdResponse.loading}
                    >
                      Confirm
                    </Button>
                  </PopoverFooter>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {status !== EmployerJobAdStatus.IN_REVIEW && (
            <Alert
              status={
                status === EmployerJobAdStatus.ACTIVE ? "success" : "error"
              }
            >
              <AlertIcon />
              This job ad has been{" "}
              {status === EmployerJobAdStatus.ACTIVE ? "activated" : "rejected"}
              .
            </Alert>
          )}
          <Divider />

          <Button variant="outline" onClick={() => history.push("/job-ads")}>
            Back to List
          </Button>
        </div>
      </div>
    </div>
  );
}
